// Canonical plan / pricing data — the single source of truth for tiers.
//
// This lives in `api/lib` (plain JS, no build step) so three consumers can read
// ONE definition instead of duplicating it:
//   1. The API container ships api/{server,lib,routes}, so billing.mjs and the
//      public /api/plans route import this directly at runtime.
//   2. The Vite frontend build does `COPY . .` then `npm run build`, so
//      src/lib/constants.ts can import this and bundle it into the SPA.
//   3. The marketing site (vouchr-web) proxies the public /api/plans endpoint,
//      so the pricing page renders whatever this file says — no third copy.
//
// Edit pricing HERE. Everything else derives from it.
//
// Fields (see src/lib/types.ts `Plan` for the typed view):
//   id           stable plan id (also the PocketBase billing.planId value)
//   name         display name
//   price        monthly USD, or null for custom/comp tiers
//   priceLabel   what the UI prints for the price
//   allowance    monthly verifications, or null for unlimited
//   seats        included seats, or null for unlimited
//   overageCents per-verification overage cost in cents, or null
//   tagline      one-line marketing line
//   features     bullet list
//   highlight    visually feature this tier (the recommended plan)
//   hidden       admin-only tier — kept out of self-serve pickers AND the public
//                /api/plans feed (assigned by hand in PocketBase)

/** @typedef {import('../../src/lib/types').Plan} Plan */

/** @type {Plan[]} */
export const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    priceLabel: '$0',
    allowance: 5,
    seats: 1,
    overageCents: null,
    tagline: 'Browse the verified pool. Unlock a few profiles a month.',
    features: ['5 unlocks / month', '1 seat', 'Verified-pool search', 'Basic activity log'],
  },
  {
    id: 'solo',
    name: 'Solo',
    price: 49,
    priceLabel: '$49',
    allowance: 40,
    seats: 1,
    overageCents: 200,
    tagline: 'For the independent recruiter who lives in the pipeline.',
    features: ['40 verifications / month', '1 seat', 'Live Vouch', 'Resume hash-match', 'Full activity log'],
  },
  {
    id: 'team',
    name: 'Team',
    price: 149,
    priceLabel: '$149',
    allowance: 150,
    seats: 5,
    overageCents: 150,
    tagline: 'A hiring team sharing one verified pipeline.',
    features: ['150 verifications / month', '5 seats', 'Shared pipeline', 'Live Vouch', 'Priority verification'],
    highlight: true,
  },
  {
    id: 'scale',
    name: 'Scale',
    price: 499,
    priceLabel: '$499',
    allowance: 600,
    seats: 20,
    overageCents: 100,
    tagline: 'High-volume hiring with audit-grade tracking.',
    features: ['600 verifications / month', '20 seats', 'Audit export', 'SSO', 'Overage at $1.00'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    priceLabel: "Let's talk",
    allowance: null,
    seats: null,
    overageCents: null,
    tagline: 'Unlimited verification, SLAs, and security review.',
    features: ['Unlimited verifications', 'Unlimited seats', 'SAML + SCIM', 'Dedicated SLA', 'Custom DPA'],
  },
  {
    // Comp tier. `hidden` keeps it out of the self-serve picker and the public
    // /api/plans feed — it's assigned by hand in PocketBase (billing.planId =
    // "black"). price 0 + allowance/seats null means everything is free and
    // unlimited (the store reads null → Infinity).
    id: 'black',
    name: 'Vouchr Black',
    price: 0,
    priceLabel: 'Complimentary',
    allowance: null,
    seats: null,
    overageCents: null,
    tagline: 'No limits. No invoice. Everything Vouchr, on the house.',
    features: [
      'Unlimited verifications',
      'Unlimited seats',
      'Live Vouch',
      'Resume hash-match',
      'Priority verification',
      'Audit export',
    ],
    hidden: true,
  },
]

/** Find a plan by id (throws-free; returns undefined when unknown). */
export const planById = (id) => PLANS.find((p) => p.id === id)

/** Monthly verification allowance per plan id, for plans that have a finite
 *  allowance (the unlimited tiers are omitted). Replaces the hand-maintained
 *  map the billing webhook used to keep in sync. */
export const PLAN_ALLOWANCE = Object.fromEntries(
  PLANS.filter((p) => p.allowance != null).map((p) => [p.id, p.allowance]),
)

/** Monthly recurring value per plan id, in cents — the canonical MRR figure the
 *  billing webhook denormalizes onto each subscription_events row so reporting
 *  (api/routes/admin.mjs) sums a stored number instead of re-deriving from
 *  Stripe. Custom/comp tiers (price null) and Free contribute 0. Keep the
 *  bootstrap migration's inline MRR_CENTS map (1700000254) in step with this. */
export const PLAN_MRR_CENTS = Object.fromEntries(
  PLANS.map((p) => [p.id, Math.round((p.price || 0) * 100)]),
)

/** MRR (cents) for a plan id; 0 for unknown/unlimited/free. */
export const mrrCentsForPlan = (id) => PLAN_MRR_CENTS[id] ?? 0

/** The plans safe to publish on the public marketing site: hidden/comp tiers
 *  are dropped. (All current fields are already public-safe, but this keeps the
 *  filter explicit for whatever fields get added later.) */
export const marketingPlans = () => PLANS.filter((p) => !p.hidden)
